import _ from 'underscore';
import queryString from 'query-string';
import type { GlobalAppServerContext, QueryParamValue } from './types';

export type ParsedQuery = ReturnType<typeof queryString.parse>;
export type QueryParams = Record<string, QueryParamValue>;

function isNullOrUndefined(value: QueryParamValue) {
  return _.isNull(value) || _.isUndefined(value);
}

function findMismatches(original: ParsedQuery, expected: QueryParams, force: boolean): Record<string, string> {
  return _.reduce(expected, (result, value, key) => {
    if ((force || _.has(original, key)) &&
      !isNullOrUndefined(value) &&
      original[key] !== `${value}`) {
      _.extend(result, { [key]: `${value}` });
    }
    return result;
  }, {} as Record<string, string>);
}

/**
 * Ensures that the params are present in the URL
 * @param {Object} params - The new query params
 * @param {Boolean} [force=false] - whether to update the URL even if the params aren't present
 * @returns {void}
 */
export function checkQueryParams(params: QueryParams, force = false) {
  const query = queryString.parse(window.location.search);

  const mismatches = findMismatches(query, params, force);

  if (!_.isEmpty(mismatches)) {
    const updatedQuery = _.extend(query, mismatches);
    // use replaceState as we just need to fix the url, no need to refresh page
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}?${queryString.stringify(updatedQuery, { sort: false })}${window.location.hash}`
    );
  }
}

// For fresh page load, in case user put wrong values for known query params (uid, cid, aid),
// this component restores it per server context
export function checkUserQueryParams(serverContext: GlobalAppServerContext, force = false) {
  const {
    CurrentUser: { Id: uid },
    CurrentCustomer: { Id: cid },
    CurrentAccount: { Id: aid },
  } = serverContext;

  const params = { uid, cid, aid };
  checkQueryParams(params, force);
}

// This function provides an easy way to trim unncessary params, e.g. skipcccache
export function deleteQueryParam(param: string) {
  const query = queryString.parse(window.location.search);
  delete query[param];
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}?${queryString.stringify(query, { sort: false })}${window.location.hash}`
  );
}
